import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Paper,
	Table,
	TableBody,
	TableContainer,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	getNotificationSubscriptions,
	getNotificationSubscriptionsLoader,
} from 'redux/reducers/settings/selectors';
import {
	getNotificationSubscriptionsRequest,
	updateNotificationSubscriptionRequest,
} from 'redux/reducers/settings/reducer';
import { StyledTableCell, StyledTableRow } from 'ui/StyledTable';
import {
	TNotificationSubscriptions,
	TNotificationSubscriptionsKey,
} from 'redux/reducers/settings/types';
import { Loader } from 'ui/Loader';
import { TAssignNotificationTypeDialogProps } from './types';

const NOTIFICATION_TYPES: { name: TNotificationSubscriptionsKey; title: string }[] = [
	{ name: 'filedIossOss', title: 'Notification of filed IOSS/OSS report by EAS' },
	{
		name: 'lodgeTheOssQuarterlyReport',
		title: 'Notification for need to lodge the OSS quarterly report',
	},
	{ name: 'invoice', title: 'Invoice notification' },
	{ name: 'lodgeTheIossReport', title: 'Notification for need to lodge the IOSS report' },
	{ name: 'outstandingInvoice', title: 'Invoice Payment Reminder' },
	{ name: 'updateOnVatCorrectionStatus', title: 'Notification of VAT Correction Status' },
	{ name: 'directMessage', title: 'Notification messages from EAS project managers' },
];

const EMPTY_SUBSCRIPTIONS: TNotificationSubscriptions = {
	filedIossOss: false,
	lodgeTheOssQuarterlyReport: false,
	invoice: false,
	lodgeTheIossReport: false,
	outstandingInvoice: false,
	updateOnVatCorrectionStatus: false,
	directMessage: false,
};

export const AssignNotificationTypeDialog: FC<TAssignNotificationTypeDialogProps> = ({
	open,
	setClose,
	data,
}) => {
	const dispatch = useDispatch();
	const notificationSubscriptions = useSelector(getNotificationSubscriptions);
	const notificationSubscriptionsIsLoading = useSelector(getNotificationSubscriptionsLoader);

	useEffect(() => {
		if (!open) return;
		if (data?.email) dispatch(getNotificationSubscriptionsRequest({ email: data.email }));
	}, [data, dispatch, open]);

	const [notificationSubscriptionState, setNotificationSubscriptionState] =
		useState<TNotificationSubscriptions>(EMPTY_SUBSCRIPTIONS);
	useEffect(() => {
		setNotificationSubscriptionState({ ...EMPTY_SUBSCRIPTIONS, ...notificationSubscriptions });
	}, [notificationSubscriptions, open]);

	const handleNotificationSubscription = (val: boolean, name: TNotificationSubscriptionsKey) => {
		setNotificationSubscriptionState((prev) => ({
			...prev,
			[name]: val,
		}));
	};

	const handleEdit = () => {
		if (!data?.email) return;
		dispatch(
			updateNotificationSubscriptionRequest({
				email: data.email,
				data: notificationSubscriptionState,
			}),
		);
		setClose();
	};

	return (
		<Dialog
			open={open}
			onClose={setClose}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
			sx={{
				'& .MuiDialog-paper': {
					minWidth: '600px',
					maxWidth: '100%',
					maxHeight: '100%',
					minHeight: '560px',
				},
			}}
		>
			<DialogTitle id="alert-dialog-title">Assign notification type</DialogTitle>
			<DialogContent>
				{notificationSubscriptionsIsLoading && (
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							width: '100%',
							height: '400px',
						}}
					>
						<Loader />
					</div>
				)}
				{notificationSubscriptions && !notificationSubscriptionsIsLoading && (
					<Paper>
						<TableContainer sx={{ borderRadius: '12px!important' }}>
							<Table>
								<TableBody>
									{NOTIFICATION_TYPES.map((item) => (
										<StyledTableRow key={item.name}>
											<StyledTableCell align="left" colSpan={1}>
												{item.title}
											</StyledTableCell>
											<StyledTableCell align="left" colSpan={1}>
												<Checkbox
													checked={notificationSubscriptionState[item.name]}
													onChange={(e) =>
														handleNotificationSubscription(e.target.checked, item.name)
													}
												/>
											</StyledTableCell>
										</StyledTableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</Paper>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={setClose}>Close</Button>
				<Button
					onClick={handleEdit}
					disabled={!notificationSubscriptions || notificationSubscriptionsIsLoading}
				>
					Edit
				</Button>
			</DialogActions>
		</Dialog>
	);
};
